import React from 'react';
import PropTypes from 'prop-types';
import { Formik, getIn } from 'formik';
import * as Yup from 'yup';

import Button from '~/components/core/Atomic/Buttons/Button';
import Grid from '~/components/core/Atomic/Grid/Grid';
import { isCountryWithStates, isFeatureEnabled } from '~/Utils';

import { isExpirationAfterEffectiveDT } from '../../PolicyUtils';
import { CONFIGURATION_FEATURES_NAMES, COUNTRY_TO_STATE_MAP } from '../../Types';
import CardDialog from '../CardDialog';
import { useClaim } from '../ClaimContainer';
import ContactTextFieldFormik from '../ContactTextFieldFormik';
import { useCms } from '../hooks/useCms';
import useGlobalAddresses from '../hooks/useGlobalAddresses';
import { getLocationFields } from '../Location';
import LocationTextFieldFormik from '../LocationTextFieldFormik';
import StateAutocompleteFormik from '../StateAutocompleteFormik';
import {
  DatePickerTextFieldFormik,
  MonetaryValueTextFieldFormik,
  TextFieldFormik,
  TimePickerTextFieldFormik,
} from '../TextFieldFormik';

import PolicyStateCountrySync, { normalizePolicyState } from './PolicyStateCountrySync';

import { useStyles } from '../../assets/styles';

function HomeManualPolicyDetailsDialog(props) {
  const { onClose, onSubmitPolicy, isExistingPolicy } = props;
  const { claim } = useClaim();
  const { classes } = useStyles();
  const { userOrganization } = useCms();

  const isConfigurableCoveragesEnabled = isFeatureEnabled(
    userOrganization,
    CONFIGURATION_FEATURES_NAMES.CONFIGURABLE_COVERAGES
  );
  const { defaultCountry } = useGlobalAddresses({ countriesConfigurationKey: 'loss_location' });

  const getHomePolicyDetailsInitialValues = () => ({
    policy_number: '',
    policy_type: '',
    policy_effective_date: '',
    effective_time: '',
    policy_expiration_date: '',
    expiration_time: '',
    underwriting_company: '',
    insured_contact_id: '',
    insured_property_location: getLocationFields(defaultCountry),
    mortgagee_contact_id: '',
    coverage_a_limit: isConfigurableCoveragesEnabled ? undefined : '',
    coverage_b_limit: isConfigurableCoveragesEnabled ? undefined : '',
    coverage_c_limit: isConfigurableCoveragesEnabled ? undefined : '',
    coverage_d_limit: isConfigurableCoveragesEnabled ? undefined : '',
    coverage_e_limit: isConfigurableCoveragesEnabled ? undefined : '',
    coverage_f_limit: isConfigurableCoveragesEnabled ? undefined : '',
    aop_deductible: '',
    ...(isCountryWithStates(defaultCountry) && { policy_state: '' }),
  });

  function getPolicyInitialValues(policy) {
    let policyInitialValues = {};
    for (const key of Object.keys(getHomePolicyDetailsInitialValues())) {
      policyInitialValues[key] = policy[key];
    }
    // policy_state visibility and validation follow the insured-property country, not the org
    // default country, so always seed it from the stored policy — otherwise editing a
    // state-bearing property under a stateless org default would drop the held value from the
    // form, and an optional (e.g. AU) save would clear the stored state. PolicyStateCountrySync
    // still normalises it to null when the property country has no states.
    policyInitialValues.policy_state = policy.policy_state ?? null;
    return policyInitialValues;
  }

  const requiredNumberForOldCoverages = isConfigurableCoveragesEnabled ? undefined : Yup.number().required('Required');

  const homePolicyDetailsValidationFields = {
    policy_number: Yup.string().required('Required'),
    policy_effective_date: Yup.string().required('Required'),
    effective_time: Yup.string()
      .matches(/^([0-1][0-9]|2[0-3]):[0-5][0-9](|:[0-5][0-9])$/, 'Invalid time supplied')
      .nullable(),
    policy_expiration_date: Yup.string()
      .required('Required')
      .test(
        'expiration_after_effective_dt',
        'Expiration date must be after effective date',
        isExpirationAfterEffectiveDT
      ),
    expiration_time: Yup.string()
      .matches(/^([0-1][0-9]|2[0-3]):[0-5][0-9](|:[0-5][0-9])$/, 'Invalid time supplied')
      .nullable(),
    insured_contact_id: Yup.number().required('Required'),
    insured_property_location: Yup.object().test('is-valid', 'Must contain address1, city', function (value) {
      // State is required only for a US property — matching the policy_state rule and the
      // Location component's US-only state field — rather than the org locale, so a US
      // property on a non-US locale can't be saved without an address state.
      const stateRequired = value.country === 'US';
      if (value.address1 && value.city && (value.state || !stateRequired)) return true;
      return this.createError({ message: `Must contain address1, city${stateRequired ? ' and state' : ''}` });
    }),
    mortgagee_contact_id: Yup.number().nullable(),
    coverage_a_limit: requiredNumberForOldCoverages,
    coverage_b_limit: requiredNumberForOldCoverages,
    coverage_c_limit: requiredNumberForOldCoverages,
    coverage_d_limit: requiredNumberForOldCoverages,
    coverage_e_limit: requiredNumberForOldCoverages,
    coverage_f_limit: requiredNumberForOldCoverages,
    aop_deductible: Yup.number().required('Required'),
    policy_state: Yup.string()
      .nullable()
      .test('policy-state-required-for-us', 'Required', function (value) {
        const country = this.parent?.insured_property_location?.country;
        // Evaluate against the country-normalised value so a state that is stale for the
        // property country can neither satisfy nor bypass the US-required rule even if
        // validation runs before the sync effect clears the field.
        const normalizedState = normalizePolicyState(country, value);
        return country === 'US' ? Boolean(normalizedState) : true;
      }),
  };

  const newHomePolicyFormikInner = (formikProps) => {
    const { isSubmitting, handleSubmit, setFieldValue, values } = formikProps;

    const propertyCountry = getIn(values, 'insured_property_location.country');

    return (
      <CardDialog isDialog={true} title="Policy Details" maxWidth="sm" onClose={onClose} preventClose={isSubmitting}>
        <PolicyStateCountrySync country={propertyCountry} />
        <Grid container spacing={1}>
          <Grid item xs={6}>
            <TextFieldFormik id="policy_number" label="Policy Number" fullWidth className={classes.textField} />
          </Grid>
          {isCountryWithStates(propertyCountry) && (
            <Grid item xs={6}>
              <StateAutocompleteFormik
                id="policy_state"
                label="Policy State"
                statesDict={COUNTRY_TO_STATE_MAP[propertyCountry]}
              />
            </Grid>
          )}
          <Grid item xs={6}>
            <ContactTextFieldFormik
              id="insured_contact"
              label="Insured"
              className={classes.textField}
              acceptedRoles={['insured']}
              fullWidth
              fixedSearchResults
              claimId={claim.Id}
            />
          </Grid>
          <Grid item xs={6}>
            <ContactTextFieldFormik
              id="mortgagee_contact"
              label="Mortgagee"
              acceptedRoles={['mortgagee']}
              className={classes.textField}
              fullWidth
              fixedSearchResults
              claimId={claim.Id}
            />
          </Grid>
          <Grid item xs={6}>
            <DatePickerTextFieldFormik
              id="policy_effective_date"
              label="Effective Date"
              fullWidth
              className={classes.textField}
            />
          </Grid>
          <Grid item xs={6}>
            <TimePickerTextFieldFormik
              id="effective_time"
              label="Effective Time"
              fullWidth
              className={classes.textField}
            />
          </Grid>
          <Grid item xs={6}>
            <DatePickerTextFieldFormik
              id="policy_expiration_date"
              label="Expiration Date"
              fullWidth
              className={classes.textField}
            />
          </Grid>
          <Grid item xs={6}>
            <TimePickerTextFieldFormik
              id="expiration_time"
              label="Expiration Time"
              fullWidth
              className={classes.textField}
            />
          </Grid>
          <Grid item xs={12}>
            <LocationTextFieldFormik
              id="insured_property_location"
              label="Insured Property Address"
              location={values['insured_property_location']}
              onChangeLocation={(location) => setFieldValue('insured_property_location', location)}
              countriesConfigurationKey="loss_location"
            />
          </Grid>
          {!isConfigurableCoveragesEnabled && (
            <>
              <Grid item xs={4}>
                <MonetaryValueTextFieldFormik
                  id="coverage_a_limit"
                  label="Coverage A Limit"
                  allowNegative={false}
                  className={classes.textField}
                />
              </Grid>
              <Grid item xs={4}>
                <MonetaryValueTextFieldFormik
                  id="coverage_b_limit"
                  label="Coverage b limit"
                  allowNegative={false}
                  className={classes.textField}
                />
              </Grid>
              <Grid item xs={4}>
                <MonetaryValueTextFieldFormik
                  id="coverage_c_limit"
                  label="Coverage c limit"
                  allowNegative={false}
                  className={classes.textField}
                />
              </Grid>
              <Grid item xs={4}>
                <MonetaryValueTextFieldFormik
                  id="coverage_d_limit"
                  label="Coverage d limit"
                  allowNegative={false}
                  className={classes.textField}
                />
              </Grid>
              <Grid item xs={4}>
                <MonetaryValueTextFieldFormik
                  id="coverage_e_limit"
                  label="Coverage e limit"
                  allowNegative={false}
                  className={classes.textField}
                />
              </Grid>
              <Grid item xs={4}>
                <MonetaryValueTextFieldFormik
                  id="coverage_f_limit"
                  label="Coverage f limit"
                  allowNegative={false}
                  className={classes.textField}
                />
              </Grid>
            </>
          )}
          <Grid item xs={4}>
            <MonetaryValueTextFieldFormik
              id="aop_deductible"
              label="AoP Deductible"
              allowNegative={false}
              className={classes.textField}
            />
          </Grid>
          <Grid item xs={12}>
            <div className={classes.buttonsContainer}>
              <Button variant="contained" color="primary" disabled={isSubmitting} onClick={handleSubmit}>
                {isExistingPolicy ? 'Save' : 'Create'}
              </Button>
            </div>
          </Grid>
        </Grid>
      </CardDialog>
    );
  };

  return (
    <Formik
      initialValues={
        isExistingPolicy
          ? {
              ...getPolicyInitialValues(claim.policy),
              type: 'home_policy',
              insured_contact_id: claim.policy.insured_contact_id,
              insured_property_location: claim.policy.insured_property_location || getLocationFields(defaultCountry),
            }
          : {
              ...getHomePolicyDetailsInitialValues(),
              type: 'home_policy',
              insured_contact_id: claim.policy.insured_contact_id,
              insured_property_location: claim.policy.insured_property_location || getLocationFields(defaultCountry),
            }
      }
      validationSchema={Yup.object().shape(homePolicyDetailsValidationFields)}
      onSubmit={async (values, formikProps) => {
        try {
          // Normalise policy_state against the property country at submit time too, so the
          // payload never carries a state that is stale for the country regardless of when the
          // sync effect runs relative to submit. When the property has no country the state
          // field is hidden and the sync effect no-ops (it returns early on a falsy country),
          // so clear the state outright — a policy_state has no basis without a country.
          const propertyCountry = getIn(values, 'insured_property_location.country');
          await onSubmitPolicy({
            ...values,
            policy_state: propertyCountry ? normalizePolicyState(propertyCountry, values.policy_state) : null,
          });
        } catch {
          formikProps.setSubmitting(false);
        }
      }}
      enableReinitialize
    >
      {(formikProps) => newHomePolicyFormikInner(formikProps)}
    </Formik>
  );
}

HomeManualPolicyDetailsDialog.propTypes = {
  onClose: PropTypes.func.isRequired,
  onSubmitPolicy: PropTypes.func.isRequired,
  isExistingPolicy: PropTypes.bool,
};

export default HomeManualPolicyDetailsDialog;
